import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { NotebookPen, Plus, Trash2, Save, X, Pencil } from "lucide-react";
import { api } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function JournalPage() {
  const { gkId } = useParams();
  const { user: me } = useAuth();
  const [gk, setGk] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [gks, jn] = await Promise.all([
        api.get("/goalkeepers"),
        api.get(`/goalkeepers/${gkId}/journal`),
      ]);
      setGk(gks.data.find((g) => g.id === gkId));
      setEntries(jn.data);
    } catch {
      toast.error("Kunde inte hämta dagboken");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [gkId]);

  const addEntry = async () => {
    if (!newBody.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/goalkeepers/${gkId}/journal`, {
        title: newTitle.trim() || null,
        body: newBody.trim(),
      });
      setEntries((e) => [data, ...e]);
      setNewTitle("");
      setNewBody("");
      setCreating(false);
      toast.success("Anteckning sparad");
    } catch {
      toast.error("Kunde inte spara anteckningen");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setEditTitle(e.title || "");
    setEditBody(e.body);
  };

  const saveEdit = async () => {
    if (!editBody.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/journal/${editId}`, {
        title: editTitle.trim() || null,
        body: editBody.trim(),
      });
      setEntries((es) => es.map((x) => (x.id === editId ? data : x)));
      setEditId(null);
      toast.success("Anteckning uppdaterad");
    } catch {
      toast.error("Kunde inte uppdatera");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/journal/${deleteId}`);
      setEntries((es) => es.filter((x) => x.id !== deleteId));
      toast.success("Anteckning borttagen");
    } catch {
      toast.error("Kunde inte ta bort");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader
        subtitle={gk?.name || "Dagbok"}
        backTo={`/gk/${gkId}`}
        backLabel="Målvakt"
      />

      <main className="max-w-[900px] mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#34C759] font-display mb-2">
              <NotebookPen size={14} /> Dagbok
            </div>
            <h1 className="font-display font-black uppercase tracking-widest text-4xl md:text-5xl text-white">
              Anteckningar
            </h1>
            <div className="text-zinc-400 mt-2">
              Kommentarer, träningsupplägg och observationer för {gk?.name || "målvakten"}.
            </div>
          </div>
          {!creating && (
            <Button
              data-testid="journal-new-btn"
              onClick={() => setCreating(true)}
              className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest h-11"
            >
              <Plus size={16} className="mr-2" />
              Ny anteckning
            </Button>
          )}
        </div>

        {creating && (
          <div
            className="card-tactical p-6 mb-6 space-y-3"
            data-testid="journal-new-form"
          >
            <Input
              data-testid="journal-new-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Rubrik (valfritt)"
              className="h-11 rounded-none border-[#3F3F46] bg-black text-white font-display uppercase tracking-wide"
            />
            <textarea
              data-testid="journal-new-body"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              placeholder="Skriv en anteckning…"
              rows={6}
              className="w-full px-3 py-2 bg-black border border-[#3F3F46] text-white text-sm focus:outline-none focus:border-[#34C759] resize-none"
            />
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setCreating(false);
                  setNewTitle("");
                  setNewBody("");
                }}
                variant="outline"
                className="rounded-none border-[#3F3F46] bg-transparent text-white font-display uppercase tracking-widest"
              >
                <X size={14} className="mr-2" />
                Avbryt
              </Button>
              <Button
                data-testid="journal-new-save"
                onClick={addEntry}
                disabled={!newBody.trim() || saving}
                className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-40"
              >
                <Save size={14} className="mr-2" />
                {saving ? "Sparar…" : "Spara"}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500 font-display uppercase tracking-widest">
            Laddar…
          </div>
        ) : entries.length === 0 && !creating ? (
          <div className="card-tactical p-16 text-center" data-testid="journal-empty">
            <div className="font-display font-black text-xl uppercase tracking-widest mb-3">
              Inga anteckningar än
            </div>
            <div className="text-zinc-400">
              Klicka på "Ny anteckning" för att börja logga dina tankar.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((e) =>
              editId === e.id ? (
                <div
                  key={e.id}
                  className="card-tactical p-6 space-y-3"
                  data-testid={`journal-edit-${e.id}`}
                >
                  <Input
                    value={editTitle}
                    onChange={(ev) => setEditTitle(ev.target.value)}
                    placeholder="Rubrik (valfritt)"
                    className="h-11 rounded-none border-[#3F3F46] bg-black text-white font-display uppercase tracking-wide"
                  />
                  <textarea
                    value={editBody}
                    onChange={(ev) => setEditBody(ev.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 bg-black border border-[#3F3F46] text-white text-sm focus:outline-none focus:border-[#34C759] resize-none"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      onClick={() => setEditId(null)}
                      variant="outline"
                      className="rounded-none border-[#3F3F46] bg-transparent text-white font-display uppercase tracking-widest"
                    >
                      Avbryt
                    </Button>
                    <Button
                      data-testid={`journal-save-${e.id}`}
                      onClick={saveEdit}
                      disabled={!editBody.trim() || saving}
                      className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-40"
                    >
                      Spara
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  key={e.id}
                  className="card-tactical p-6"
                  data-testid={`journal-entry-${e.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      {e.title && (
                        <div className="font-display font-black uppercase tracking-widest text-lg text-white mb-1">
                          {e.title}
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                        {format(new Date(e.created_at), "d MMM yyyy · HH:mm", {
                          locale: sv,
                        })}
                        {e.author_name && (
                          <span className="ml-2 text-[#34C759]">
                            · {e.author_name}
                          </span>
                        )}
                        {e.updated_at && " · redigerad"}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {(gk?.role === "owner" || e.author_id === me?.id) && (
                        <>
                          <button
                            data-testid={`journal-edit-btn-${e.id}`}
                            onClick={() => startEdit(e)}
                            className="p-2 border border-[#27272A] text-zinc-400 hover:bg-[#171717] hover:text-white"
                            title="Redigera"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            data-testid={`journal-delete-btn-${e.id}`}
                            onClick={() => setDeleteId(e.id)}
                            className="p-2 border border-[#27272A] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                            title="Ta bort"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed">
                    {e.body}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </main>

      <AlertDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
      >
        <AlertDialogContent className="bg-[#111] border-[#27272A] text-white rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest">
              Ta bort anteckning?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Anteckningen försvinner permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white">
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-none bg-[#FF3B30] hover:bg-[#e33228] text-white font-display uppercase tracking-widest"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
